/** @format */

import { IconButton } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FileDropZone from '../FileDropZone';
import { DOCUMENT_MAX_FILES } from 'src/constants';
import { GA4_DOCUMENT_CATEGORY } from 'src/analytics';
import { buildDocumentDownloadParams } from 'src/analytics/buildGa4EventPayload';
import { CloseIcon, DownloadIcon, UploadIcon, ViewIcon } from 'src/components/icons';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import type { ContentDocument, ContentEditorPart, ContentPartUploadFn, PatchContentPartFn } from 'src/types';

function formatFileSize(bytes: number | undefined | null): string {
  if (bytes == null || typeof bytes !== 'number' || Number.isNaN(bytes)) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(2)} KB`;
  }
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

function fileExtensionBadge(name: string | undefined): string {
  if (!name || !name.includes('.')) {
    return 'FILE';
  }
  const ext = name.split('.').pop()?.toUpperCase() ?? '';
  return ext.length > 4 ? ext.slice(0, 4) : ext;
}

function documentRowKey(d: ContentDocument, index: number): string {
  if (d.localId != null) return String(d.localId);
  if (d.id != null) return `id-${d.id}`;
  return `idx-${index}`;
}

interface DocumentEntry {
  localId: string;
  file?: File | null;
  name: string;
  size?: number;
  remoteUrl?: string;
}

interface DocumentFileCardProps {
  entry: DocumentEntry;
  onRemove: (localId: string) => void;
  viewLabel: string;
  downloadLabel: string;
  removeLabel: string;
}

function DocumentFileCard({ entry, onRemove, viewLabel, downloadLabel, removeLabel }: DocumentFileCardProps) {
  const { trackDocumentDownload } = useGa4Analytics();
  const badge = fileExtensionBadge(entry.name);
  const remoteUrl = typeof entry.remoteUrl === 'string' && entry.remoteUrl.trim() ? entry.remoteUrl.trim() : null;

  const handleView = useCallback(() => {
    if (remoteUrl) {
      window.open(remoteUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!entry.file) return;
    const url = URL.createObjectURL(entry.file);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }, 60_000);
  }, [entry.file, remoteUrl]);

  const handleDownload = useCallback(() => {
    trackDocumentDownload(
      buildDocumentDownloadParams({
        document_name: entry.name,
        url: remoteUrl || entry.name,
        document_category: GA4_DOCUMENT_CATEGORY.CONTENT_EDITOR
      })
    );

    if (remoteUrl) {
      const a = document.createElement('a');
      a.href = remoteUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.download = entry.name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    if (!entry.file) return;
    const url = URL.createObjectURL(entry.file);
    const a = document.createElement('a');
    a.href = url;
    a.download = entry.name || 'document';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }, 2000);
  }, [entry.file, entry.name, remoteUrl, trackDocumentDownload]);

  return (
    <div className="content-editor__document-card">
      <div className="content-editor__document-card-top">
        <span className="content-editor__document-badge">{badge}</span>
        <div className="content-editor__document-card-actions">
          <IconButton size="small" aria-label={viewLabel} onClick={handleView}>
            <ViewIcon width={18} height={18} />
          </IconButton>
          <IconButton size="small" aria-label={downloadLabel} onClick={handleDownload}>
            <DownloadIcon width={18} height={18} />
          </IconButton>
          <IconButton size="small" aria-label={removeLabel} onClick={() => onRemove(entry.localId)}>
            <CloseIcon width={14} height={14} />
          </IconButton>
        </div>
      </div>
      <div className="content-editor__document-card-bottom">
        <span className="content-editor__document-name">{entry.name}</span>
        <span className="content-editor__document-size">{formatFileSize(entry.size)}</span>
      </div>
    </div>
  );
}

interface ContentEditorBlockBodyDocumentProps {
  part: ContentEditorPart;
  onPatch: PatchContentPartFn;
  onFile?: ContentPartUploadFn;
}

export default function ContentEditorBlockBodyDocument({
  part,
  onPatch,
  onFile = undefined
}: ContentEditorBlockBodyDocumentProps) {
  const { t } = useTranslation();
  const documentsPart = part.type === 'documents' ? part : null;

  const rows = useMemo(() => {
    const docs = Array.isArray(documentsPart?.documents) ? documentsPart.documents : [];
    return docs.map((d, index) => {
      const localId = documentRowKey(d, index);
      const name = d.name || d.title || 'document';
      const remoteUrl = d.remoteUrl || (typeof d.url === 'string' ? d.url : undefined);
      return {
        ...d,
        localId,
        name,
        remoteUrl,
        file: d.file || null,
        size: typeof d.size === 'number' ? d.size : undefined
      };
    });
  }, [documentsPart?.documents]);

  const removeFile = useCallback(
    (localId: string) => {
      const docs = Array.isArray(documentsPart?.documents) ? [...documentsPart.documents] : [];
      const next = docs.filter((d, index) => documentRowKey(d, index) !== String(localId));
      onPatch(part.id, { documents: next });
    },
    [documentsPart?.documents, onPatch, part.id]
  );

  const viewLabel = t('contentEditor.documentViewAria');
  const downloadLabel = t('contentEditor.documentDownloadAria');
  const removeLabel = t('contentEditor.documentRemoveAria');

  const uploadLabel = t('contentEditor.uploadDocumentLabel');

  return (
    <div className="content-editor__document-grid">
      {rows.map(entry => (
        <DocumentFileCard
          key={entry.localId}
          entry={entry}
          onRemove={removeFile}
          viewLabel={viewLabel}
          downloadLabel={downloadLabel}
          removeLabel={removeLabel}
        />
      ))}

      {rows.length < DOCUMENT_MAX_FILES && (
        <div className={`content-editor__document-slot content-editor__document-slot--drop`}>
          <FileDropZone
            inputId={`content-editor-file-${part.id}`}
            accept=".pdf,.doc,.docx,.xlsx,.txt"
            className="content-editor__dropzone--document-upload"
            leading={<UploadIcon width={22} height={22} />}
            label={uploadLabel}
            fileName=""
            multiple
            onFile={onFile}
          />
        </div>
      )}
    </div>
  );
}
